import convert from "convert-units";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import Avatar from "../../../../parts/avatar";
import SvgIcon from "../../../../parts/SvgIcon";
import { useRillContext } from "../../../../rill/RillReact/share/base";
import getAvatarUrl from "../../../../utils/get-avatar-url";

const LeaderCards = ({ participants }) => {
  const { t } = useTranslation("coaching-portal");
  const {
    locals: { activeUser },
  } = useRillContext();
  const units = activeUser.units || "metric";
  const leaders = useMemo(() => {
    if (!participants.length) return [];

    const findLeader = (id, getValue, formatValue, labelKey, icon, color) => {
      let best = null;
      let bestVal = -Infinity;
      for (const p of participants) {
        const val = getValue(p);
        // Skip empty values so a card never crowns a leader with nothing to show.
        if (val != null && val > 0 && val > bestVal) {
          bestVal = val;
          best = p;
        }
      }
      if (!best) return null;
      return {
        id,
        labelKey,
        icon,
        color,
        name: best.name,
        avatar: best.avatar,
        value: formatValue(bestVal),
      };
    };

    return [
      findLeader(
        "workouts",
        (p) => p.totalWorkoutDays,
        (v) => t("challengeReport.leaders.workoutsCount", { count: v }),
        "mostWorkouts",
        "/img/new-icons/barbell-fill.svg",
        "#5EC165"
      ),
      findLeader(
        "steps",
        (p) => p.avgDailySteps,
        (v) =>
          t("challengeReport.leaders.stepsAvgPerDay", {
            value: v.toLocaleString(),
          }),
        "mostSteps",
        "/img/new-icons/person-walking-fill.svg",
        "#4E81F2"
      ),
      findLeader(
        "habits",
        (p) => p.habitsAdherence,
        (v) => `${v.toFixed(1)}%`,
        "bestHabits",
        "/img/new-icons/habits.svg",
        "#EA7B87"
      ),
      findLeader(
        "bw",
        (p) =>
          p.bodyweight?.change == null ? null : Math.abs(p.bodyweight.change),
        (v) => {
          const converted =
            units === "imperial" ? convert(v).from("kg").to("lb") : v;
          return `${converted.toFixed(1)} ${units === "imperial" ? "lb" : "kg"}`;
        },
        "biggestBwChange",
        "/img/new-icons/scale.svg",
        "#7AB555"
      ),
    ].filter(Boolean);
  }, [participants, units, t]);

  if (!leaders.length) return null;

  return (
    <section className="challenge-report__leaders">
      {leaders.map((leader) => (
        <div className="challenge-report__leader-card" key={leader.id}>
          <div className="challenge-report__leader-card-header">
            <SvgIcon src={leader.icon} noFilterOnLight />
            <span
              className="challenge-report__leader-label"
              style={{ color: leader.color }}
            >
              {t(`challengeReport.leaders.${leader.labelKey}`)}
            </span>
          </div>
          <div className="challenge-report__leader-info">
            <div className="challenge-report__leader-avatar">
              <Avatar
                size="44"
                url={getAvatarUrl({ avatar: { s3Path: leader.avatar } })}
                user={leader}
              />
            </div>
            <div>
              <p className="challenge-report__leader-name">{leader.name}</p>
              <p className="challenge-report__leader-value">{leader.value}</p>
            </div>
          </div>
        </div>
      ))}
    </section>
  );
};

export default LeaderCards;
